import { useQuery } from '@tanstack/react-query'

import type { ClinicId } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

const PATIENT_LIMIT = 200

/**
 * Fetches the checkout queue for a clinic — orders belonging to patients with
 * a today-appointment. The server returns pending, prepared, and invoiceable
 * orders in a single response.
 */
export function useCheckoutQueue(opts: {
  clinicId: ClinicId | undefined
  q?: string
  page?: number
}) {
  const api = useOsApi()
  const { clinicId, q, page = 1 } = opts

  const { data, isLoading, error } = useQuery({
    queryKey: ['orders', 'checkout', { clinicId, q, page }],
    queryFn: async () => {
      const res = await api.get('/orders/checkout', {
        query: {
          clinicId: clinicId!,
          q,
          patientLimit: PATIENT_LIMIT,
          patientOffset: (page - 1) * PATIENT_LIMIT,
        },
      })
      if (!res.success) throw new Error(res.error.message)
      return res.data
    },
    enabled: !!clinicId,
    refetchInterval: 20_000,
  })

  return {
    orders: data?.orders,
    totalPatientCount: data?.totalPatientCount ?? 0,
    isLoading,
    error,
  }
}
